import { ref, watchEffect } from 'vue'
import { defineStore } from 'pinia';
import { useProductStore } from '@/stores/ProductStore';
import { useMapStore } from  '@/stores/MapStore';
import { isWebGLSupported } from '@/utils/webglSupport'
import { debugCustomLayers } from '@/utils/customLayerDebug'
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals'
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'

const { $BASEURL } = useConfigurationGlobals();
const {
  $CUSTOM_LAYER_WEBGL,
  $CUSTOM_LAYER_CLICK_EVENTS,
  $CUSTOM_LAYER_HOVER_HIGHLIGHT,
  $CUSTOM_LAYER_LABELS
} = useMapConfigurationGlobals()

export let useMapCustomLayerStore = defineStore('mapCustomLayerStore', () => {
  const getFileBaseUrl = `${$BASEURL}/common/getCustomLayer?file=`;
  let map = window.$JWX.map;
  
  const productStore = useProductStore();
  const mapStore = useMapStore();

  const colors = [
    { hex: '#e6194b', label: 'red' },
    { hex: '#009eba', label: 'teal' },
    { hex: '#3cb44b', label: 'green' },
    { hex: '#ffe119', label: 'yellow' },
    { hex: '#4363d8', label: 'blue' },
    { hex: '#f58231', label: 'orange' },
    { hex: '#911eb4', label: 'purple' },
    { hex: '#46f0f0', label: 'cyan' },
    { hex: '#f032e6', label: 'magenta' },
    { hex: '#bcf60c', label: 'lime' },
    { hex: '#fabebe', label: 'pink' },
    { hex: '#008080', label: 'dark-teal' },
    { hex: '#e6beff', label: 'lavender' },
    { hex: '#9a6324', label: 'brown' },
    { hex: '#fffac8', label: 'cream' },
    { hex: '#800000', label: 'maroon' },
    { hex: '#aaffc3', label: 'mint' },
    { hex: '#808000', label: 'olive' }
  ];

  const getSVGMarker = (color) => ({
    path: 'M5.61719 13.68542C6.95313 11.92539 10 7.65967 10 5.26362 10 2.35766 7.76042 0 5 0S0 2.35766 0 5.26362c0 2.39605 3.04688 6.66177 4.38281 8.4218.32031.41944.91406.41944 1.23438 0ZM5 3.50908c.92047 0 1.66667.78554 1.66667 1.75454S5.92047 7.01816 5 7.01816c-.92047 0-1.66667-.78553-1.66667-1.75454 0-.969.7462-1.75454 1.66667-1.75454Z',
    fillColor: color,
    fillOpacity: 0.6,
    strokeWeight: 1.5,
    rotation: 0,
    scale: 2,
    anchor: new google.maps.Point(5, 14)
  })

  const canvasOverlays = [];

  const customLayers = ref([])
  const activeCustomLayers = ref(new Set())
  // const [activeCustomLayers] = useStorageSystem([], 't1ActiveCustomLayers', 'localStorage')
  
  const activateCustomLayer = async (id, fitbounds = false) => {
    const customLayer = customLayers.value.find(layer => layer.id === id);
    let canvasOverlay;
    
    if (!customLayer || customLayer.isActive) return;
    
    customLayer.isActive = true;    
    customLayer.loading = true;
    
    if (customLayer.overlayIndex === undefined || customLayer.overlayIndex === null) {
      const style = {
        fillColor: customLayer.color,
        strokeColor: customLayer.color,
        strokeWeight: 2,
        fillOpacity: 0.2,
        strokeOpacity: 1,
        visible: true,
        icon: {
          url: customLayer.icon,
          scaledSize: new google.maps.Size(18, 24),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(0, 24),
        },
        zIndex: 21
      }

      // Both overlay implementations share the same contract: {url}
      // source, ready promise, status callback, and normalized feature
      // events. On the WebGL path, fetch/parse/triangulation happen in a
      // Web Worker so large files don't block the UI.
      //
      // The implementations are lazy chunks: nobody pays for the overlay
      // code until a layer is activated, and the non-WebGL fallback is
      // only fetched by browsers that need it. import() caches, so only
      // the first activation loads the module; the delay hides behind
      // the layer's loading state.
      try {
        const OverlayClass = $CUSTOM_LAYER_WEBGL && isWebGLSupported()
          ? (await import('@/utils/CanvasGeoJsonOverlay')).CanvasGeoJsonOverlay
          : (await import('@/utils/NativeGeoJsonOverlay')).NativeGeoJsonOverlay;

        canvasOverlay = new OverlayClass(map, { url: customLayer.url }, style, {
          onStatus: (status) => (customLayer.status = status),
          interactive: $CUSTOM_LAYER_CLICK_EVENTS !== false,
          hoverHighlight: $CUSTOM_LAYER_HOVER_HIGHLIGHT !== false,
          labels: $CUSTOM_LAYER_LABELS === false ? false : {}
        });

        await canvasOverlay.ready;
      } catch (error) {
        console.warn(error);
        if (canvasOverlay) canvasOverlay.setMap(null);
        customLayer.status = 'error';
        customLayer.isActive = false;
        customLayer.loading = false;

        return
      }

      // Without click events there are no feature clicks to tell apart,
      // so the background listener would fire on every map click
      if ($CUSTOM_LAYER_CLICK_EVENTS !== false) ensureBackgroundClickListener();

      // Feature clicks (both overlay implementations emit the same event)
      // are re-broadcast on the app event bus for any UI to consume
      google.maps.event.addListener(canvasOverlay, 'click', (payload) => {
        // Mark the DOM event so the background-click listener knows this
        // map click landed on a feature
        if (payload.domEvent) consumedFeatureClicks.add(payload.domEvent);

        debugCustomLayers('dispatching custom-layer-feature-click', payload.featureIndex, payload.properties);
        window.$EventBus.dispatchEvent('custom-layer-feature-click', {
          layerId: customLayer.id,
          layerName: customLayer.name,
          featureIndex: payload.featureIndex,
          properties: payload.properties,
          latLng: payload.latLng
        });
      });

      canvasOverlays.push(canvasOverlay);
      customLayer.overlayIndex = canvasOverlays.length - 1;
    } else {
      canvasOverlay = canvasOverlays[customLayer.overlayIndex];
      canvasOverlay.setMap(map);
      customLayer.status = 'ready';
    }
    
    if (fitbounds) setLayerBounds(canvasOverlay);
    
    setTimeout(() => (customLayer.loading = false), 1) // Allow time for map with custom layer to render
    activeCustomLayers.value.add(customLayer.id);
    mapStore.state.customLayers.add(customLayer.id);
    mapStore.state.globalOverlays.add('custom-layers');
  }

  const deactivateCustomLayer = (id) => {

    const customLayer = customLayers.value.find(layer => layer.id === id);
    
    if (!customLayer) return;

    customLayer.loading = true;
        
    setTimeout(() => {
      canvasOverlays[customLayer.overlayIndex].setMap(null);

      if (customLayer.clearData) {
        canvasOverlays[customLayer.overlayIndex] = null;
        delete customLayer.overlayIndex;
        customLayer.status = 'idle';
      }
    }, 1) // Allow time for map to render

    setTimeout(() => {
      customLayer.isActive = false;
      customLayer.loading = false;
      activeCustomLayers.value.delete(customLayer.id);
      mapStore.state.customLayers.delete(customLayer.id);
      if (!mapStore.state.customLayers.size) mapStore.state.globalOverlays.delete('custom-layers');
    }, 2) // Allow time for map to render
  }

  // DOM events of map clicks that hit a feature on some active layer —
  // used to tell background clicks apart from feature clicks
  const consumedFeatureClicks = new WeakSet();
  let backgroundClickListener = null;

  /**
   * Emit 'custom-layer-background-click' when the map is clicked away
   * from every active layer's features. WebGL overlays don't consume map
   * clicks (their canvases are pointer-events: none), so feature clicks
   * are filtered out via consumedFeatureClicks; the native Data layer
   * consumes feature clicks itself, so its map clicks are backgrounds by
   * definition. Feature handlers run synchronously within the map click,
   * so a one-tick deferral sees all of them regardless of listener order.
   */
  const ensureBackgroundClickListener = () => {
    if (backgroundClickListener) return;

    backgroundClickListener = google.maps.event.addListener(map, 'click', (event) => {
      setTimeout(() => {
        if (!activeCustomLayers.value.size) {
          debugCustomLayers('background click skipped: no active layers');
          return;
        }
        if (event.domEvent && consumedFeatureClicks.has(event.domEvent)) {
          debugCustomLayers('background click skipped: click hit a feature');
          return;
        }

        debugCustomLayers('dispatching custom-layer-background-click');
        window.$EventBus.dispatchEvent('custom-layer-background-click', { latLng: event.latLng });
      }, 0);
    });
  }

  const getCustomLayers = async (refetch = false) => {
    if (customLayers.value.length > 0 && !refetch) return;

    customLayers.value = [];

    await productStore.getRegionalProducts();

    productStore.customLayers.map((layer, i) => {
      const { name, custom_layer_id, file_name } = layer;

      customLayers.value.push({
        id: custom_layer_id,
        name: name,
        fileName: file_name,
        url: `${getFileBaseUrl}${file_name}`,
        type: 'geojson',
        color: colors[i % colors.length].hex,
        icon: getMarkerIconURL(colors[i % colors.length].label),
        geojson: null,
        isActive: false,
        loading: false,
        status: 'idle'
      })
    })
  }

  const setLayerBounds = (canvasOverlay) => {
    const bounds = canvasOverlay.getBounds();
    if (bounds && !bounds.isEmpty()) {
      map.fitBounds(bounds);
    }
  }

  const activateSavedCustomLayers = async () => {
    if (!mapStore.state.globalOverlays.has('custom-layers') || !mapStore.state.customLayers.size) return;

    await getCustomLayers();

    mapStore.state.customLayers.forEach((layer) => activateCustomLayer(layer))
  }

  const setMapVar = (mapRef) => {
    map = mapRef;
  }

  const getMarkerIconURL = (color) => `https://cdn-consumer.weatherflow.com/jWeather/images/custom-layers/spot-markers/custom-layer-spot-marker-${color}@2x.png`

  watchEffect(() => {
      activeCustomLayers.value.forEach((id) => !mapStore.state.customLayers.has(id) && deactivateCustomLayer(id));

      mapStore.state.customLayers.forEach((id) => !activeCustomLayers.value.has(id) && activateCustomLayer(id));
  });

  return {
    activeCustomLayers,
    customLayers,
    activateCustomLayer,
    activateSavedCustomLayers,
    deactivateCustomLayer,
    getCustomLayers,
    setMapVar
  }
});
